import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";
import { stockOpnameService } from "@/services/stock-opname";

import type {
  CreateStockOpnameReq,
  StockOpname,
  GetStockOpnameListReq,
  UpdateStockOpnameReq,
} from "@artisancode/api-types";

export function useStockOpnames(params?: GetStockOpnameListReq) {
  return useQuery({
    queryKey: queryKeys.stockOpnames.list(params as Record<string, unknown>),
    queryFn: () => stockOpnameService.list(params),
  });
}

export function useStockOpname(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.stockOpnames.detail(id ?? ""),
    queryFn: () => stockOpnameService.detail(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateStockOpname() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      body,
      idempotencyKey,
    }: {
      body: CreateStockOpnameReq;
      idempotencyKey?: string;
    }) => stockOpnameService.create(body, idempotencyKey),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.stockOpnames.all }),
  });
}

export function useUpdateStockOpname() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & UpdateStockOpnameReq) =>
      stockOpnameService.update(id, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.stockOpnames.all }),
  });
}

// Adding/removing lines returns the updated session; seed the detail cache with it so the
// counting page shows the change without waiting for a refetch.
function useStockOpnameLinesMutation<T>(fn: (args: T) => Promise<StockOpname>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (opname) => {
      qc.setQueryData(queryKeys.stockOpnames.detail(opname.id), opname);
      qc.invalidateQueries({ queryKey: queryKeys.stockOpnames.all });
    },
  });
}

export function useAddStockOpnameItems() {
  return useStockOpnameLinesMutation(
    ({ id, itemIds }: { id: string; itemIds: string[] }) =>
      stockOpnameService.addItems(id, { itemIds }),
  );
}

export function useRemoveStockOpnameItem() {
  return useStockOpnameLinesMutation(
    ({ id, lineId }: { id: string; lineId: string }) =>
      stockOpnameService.removeItem(id, lineId),
  );
}

// Completing or cancelling posts/reverts ledger movements, so stock balances change too.
function useStockOpnameLedgerMutation<T>(fn: (args: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.stockOpnames.all });
      qc.invalidateQueries({ queryKey: queryKeys.stocks.all });
    },
  });
}

export function useCompleteStockOpname() {
  return useStockOpnameLedgerMutation(
    ({ id, items }: { id: string } & UpdateStockOpnameReq) =>
      stockOpnameService.complete(id, { items }),
  );
}

export function useCancelStockOpname() {
  return useStockOpnameLedgerMutation(
    ({ id, cancelReason }: { id: string; cancelReason: string }) =>
      stockOpnameService.cancel(id, { cancelReason }),
  );
}
